import { GRANTS, type Role, type Service, SERVICES } from "@atzmai/schema";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { api, errorText } from "../../lib/api.ts";
import { useApi } from "../../lib/useApi.ts";
import { EmailCode } from "../public/EmailCode.tsx";

interface Me {
  user: { email: string; internal: boolean };
  roles: string[];
  orgs: { id: string; name: string; kind: string; status: string; role: string }[];
  services: { id: string; label: string }[];
}

const SERVICE_LINKS: Partial<Record<Service, string>> = { download_iso: "/download" };

const KIND_WORDS: Partial<Record<Role, string>> = { customer: "business", school: "school", reseller: "reseller", tester: "tester" };

const STATUS_WORDS: Record<string, string> = { pending: "Waiting for approval", approved: "Approved", rejected: "Not approved", blocked: "Blocked" };

/** Which kinds of account open a service this one hasn't got, in plain words. */
function opensWith(service: Service) {
  const kinds = (Object.keys(GRANTS) as Role[]).filter((r) => r !== "downloader" && GRANTS[r].includes(service)).map((r) => KIND_WORDS[r]).filter(Boolean) as string[];
  if (!kinds.length) return null;
  return `With a ${kinds.length > 1 ? `${kinds.slice(0, -1).join(", ")} or ${kinds.at(-1)}` : kinds[0]} account`;
}

export function Account() {
  const me = useApi<Me>("/api/account/me");
  const navigate = useNavigate();

  if (me.loading && !me.data) return <div className="mt-10 h-40 max-w-xl animate-pulse rounded-[20px] bg-glass" aria-label="Loading" />;
  if (me.error?.status === 401) {
    return (
      <div className="flex flex-col gap-10 pt-4 md:pt-10">
        <header className="flex max-w-[38rem] flex-col gap-4">
          <h1 className="text-[clamp(36px,5.2vw,56px)] leading-[1.06] tracking-[-.02em]">Sign in to your account</h1>
          <p className="text-[20px] leading-snug text-ink-2">
            Use the email you confirmed when you downloaded. Schools, resellers and testers sign in the same way.
          </p>
        </header>
        <EmailCode mode="signin" onDone={() => void me.reload()} />
        <p className="text-ink-3">
          New here? Your account is made when you <Link to="/download">download Atzmai OS</Link>.
        </p>
      </div>
    );
  }
  if (me.error || !me.data) return <p role="alert" className="text-bad">{me.error?.message}</p>;
  const { user, orgs, services } = me.data;
  const have = new Set(services.map((s) => s.id));
  const more = (Object.keys(SERVICES) as Service[]).filter((s) => !have.has(s) && opensWith(s));

  return (
    <div className="flex flex-col gap-20 pt-4 md:pt-10">
      <header className="flex flex-wrap items-center justify-between gap-6">
        <div className="flex min-w-0 items-center gap-5">
          <span aria-hidden="true" className="avatar">{user.email[0]?.toUpperCase()}</span>
          <div className="flex min-w-0 flex-col">
            <span className="text-ink-3">Signed in as</span>
            <h1 className="text-[clamp(26px,3.6vw,40px)] leading-tight [overflow-wrap:anywhere]">{user.email}</h1>
          </div>
        </div>
        <button className="btn" onClick={async () => { await api("/api/auth/signout", { json: {} }); navigate("/"); }}>Sign out</button>
      </header>

      <section className="grid gap-8 md:grid-cols-[14rem_minmax(0,1fr)] md:gap-12" aria-labelledby="can">
        <h2 id="can" className="text-[clamp(22px,2.6vw,28px)] leading-tight">What you can do</h2>
        <div className="flex flex-col">
          {services.map((s) => {
            const to = SERVICE_LINKS[s.id as Service];
            return (
              <div key={s.id} className="flex min-h-16 items-center justify-between gap-4 border-t border-line py-3">
                <span className="flex items-center gap-3 text-[17px] text-ink">
                  <span aria-hidden="true" className="size-2 flex-none rounded-full bg-good" />{s.label}
                </span>
                {to ? <Link className="btn" to={to}>Open</Link> : <span className="text-[14px] whitespace-nowrap text-ink-3">Not ready yet</span>}
              </div>
            );
          })}
          {more.length > 0 && (
            <>
              <h3 className="mt-8 mb-1 text-[16px] text-ink-2">With another kind of account</h3>
              {more.map((s) => (
                <div key={s} className="flex min-h-14 flex-wrap items-center justify-between gap-x-4 gap-y-0.5 border-t border-line py-3">
                  <span className="flex items-center gap-3 text-ink-2">
                    <span aria-hidden="true" className="size-2 flex-none rounded-full border border-rim" />{SERVICES[s]}
                  </span>
                  <span className="pl-5 text-[14px] text-ink-3">{opensWith(s)}</span>
                </div>
              ))}
            </>
          )}
        </div>
      </section>

      <section className="grid gap-8 md:grid-cols-[14rem_minmax(0,1fr)] md:gap-12" aria-labelledby="orgs">
        <h2 id="orgs" className="text-[clamp(22px,2.6vw,28px)] leading-tight">Organisations</h2>
        {orgs.length ? (
          <div className="flex flex-col">
            {orgs.map((o) => (
              <div key={o.id} className="flex min-h-16 flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-line py-3">
                <span className="flex flex-col">
                  <span className="text-[17px] text-ink">{o.name}</span>
                  <span className="text-[14px] text-ink-3 capitalize">{o.kind}, {o.role === "admin" ? "you look after it" : "member"}</span>
                </span>
                <span className={`pill ${o.status === "approved" ? "bg-good/15 text-good" : o.status === "pending" ? "bg-warn/15 text-warn" : "bg-bad/15 text-bad"}`}>
                  {STATUS_WORDS[o.status] ?? o.status}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="max-w-[36rem] text-ink-2 md:pt-1">
            You're not part of a school or reseller yet. Apply below, or ask your organisation's admin to invite this address.
          </p>
        )}
      </section>

      <Apply />
    </div>
  );
}

const KINDS = [
  { id: "school", title: "A school", body: "Register the school's computers, get offline bundles and support." },
  { id: "reseller", title: "A reseller", body: "Preload Atzmai OS on computers you sell, with activation codes in bulk." },
  { id: "tester", title: "A tester", body: "Get early builds on the testers channel and tell us what breaks." },
] as const;

function Apply() {
  const [kind, setKind] = useState<(typeof KINDS)[number]["id"]>("school");
  const [org, setOrg] = useState("");
  const [country, setCountry] = useState("UG");
  const [seats, setSeats] = useState(10);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api("/api/account/applications", {
        json: kind === "tester" ? { kind, note: note || undefined } : { kind, org_name: org, country, seats, note: note || undefined },
      });
      setMsg({ ok: true, text: "Application sent. The team usually decides within two working days, and we'll email you." });
    } catch (err) {
      setMsg({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="grid gap-8 md:grid-cols-[14rem_minmax(0,1fr)] md:gap-12" aria-labelledby="apply">
      <div className="flex flex-col gap-2">
        <h2 id="apply" className="text-[clamp(22px,2.6vw,28px)] leading-tight">Apply for more</h2>
        <p className="text-[15px] text-ink-3">Approval adds to this same account. You keep signing in with this email.</p>
      </div>
      <form onSubmit={submit} className="flex flex-col gap-6">
        <fieldset className="m-0 grid gap-3 border-0 p-0 sm:grid-cols-3">
          <legend className="mb-3 text-[15px] font-medium">I'm applying as</legend>
          {KINDS.map((k) => (
            <label key={k.id} className="choice">
              <input type="radio" name="kind" value={k.id} checked={kind === k.id} onChange={() => setKind(k.id)} className="sr-only" />
              <span className="font-display text-[17px] font-medium text-ink">{k.title}</span>
              <span className="text-[14px] leading-snug text-ink-2">{k.body}</span>
            </label>
          ))}
        </fieldset>
        {kind !== "tester" && (
          <div className="grid gap-4 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
            <label className="flex flex-col gap-1.5 text-[15px]">Organisation name<input className="input" required value={org} onChange={(e) => setOrg(e.target.value)} /></label>
            <label className="flex flex-col gap-1.5 text-[15px]">Country code<input className="input uppercase" required maxLength={2} value={country} onChange={(e) => setCountry(e.target.value.toUpperCase())} /></label>
            <label className="flex flex-col gap-1.5 text-[15px]">Computers<input className="input" type="number" min={1} value={seats} onChange={(e) => setSeats(Number(e.target.value))} /></label>
          </div>
        )}
        <label className="flex flex-col gap-1.5 text-[15px]"><span>Anything we should know <span className="text-ink-3">(optional)</span></span>
          <textarea className="input" rows={3} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        {msg && <p role="status" className={msg.ok ? "text-good" : "text-bad"}>{msg.text}</p>}
        <button className="btn btn-primary self-start" disabled={busy}>{busy ? "Sending…" : "Send application"}</button>
      </form>
    </section>
  );
}
